/**
 * DXF → map registration (rung 3, R3-B).
 *
 * The ingest worker emits geometry in RAW DXF coordinates — registration
 * happens here, where the matching data lives. Two paths:
 *
 * 1. LAYOUT fit (preferred): the site's vector layout carries text entities at
 *    known PDF-point positions, and the DXF carries the same labels at DXF
 *    coordinates (both were extracted from the same drawing). Matching label
 *    text that is UNIQUE on both sides yields anchor pairs; a least-squares
 *    similarity (the DXF is a rotated/scaled copy of the sheet — proven on
 *    Kfar: uniform scale ≈113.5 pt/unit, 90° rotation) maps DXF → layout
 *    PDF points, and the layout's own anchor-fitted transform takes it to the
 *    map. One registration per site, everything aligned — the convergence
 *    contract from the sharp-labels phase.
 *
 * 2. EXTENTS fallback (no layout, or too few matches): stretch the DXF's
 *    global extents onto the plan overlay's quad. Rough by construction
 *    (content extents ≠ page edges), but it puts the linework on screen where
 *    the user can see what they have.
 *
 * Everything here is pure math on plain data — unit-tested, no map, no store.
 */

import { transformPoint } from '../domain/layout/transform';
import type { LayoutTextEntity, LayoutTransform } from '../domain/layout/types';

/** One text entity from the ingest's texts.json. */
export interface DxfTextEntry {
  t: string;
  x: number;
  y: number;
  /** rotation, degrees (v2+) */
  r?: number;
  /** text height, DXF units (v2+) */
  h?: number;
}

export interface DxfLayoutRegistration {
  kind: 'layout';
  /** The layout whose label positions anchored the fit (and whose transform
   *  renders it — if that layout is deleted, the fit dies with it). */
  layoutId: string;
  /** DXF units → layout PDF points, [a, b, tx, c, d, ty] (the same affine
   *  layout LayoutTransform.matrix uses): X = a·x + b·y + tx, Y = c·x + d·y + ty. */
  matrix: [number, number, number, number, number, number];
  /** Anchor pairs that survived outlier trimming. */
  matched: number;
  /** RMS residual over the surviving pairs, in PDF points. */
  rmsPt: number;
  /** Fingerprint of the label set the fit was computed against — the fit is
   *  a SNAPSHOT, and Kalia proved it silently rots when labels change after
   *  alignment (re-imports, Fix/Undo churn): labels rendered live while the
   *  DXF rode a frame 1.5-5m stale. A mismatch (or absence, for legacy
   *  registrations) triggers an automatic re-fit. */
  entitySig?: string;
  /** Fit-algorithm version — a registration computed by an older algorithm
   *  is re-healed even when its entitySig matches (Kalia's bad-basin frame
   *  had a fresh sig; only the algorithm changed). */
  algo?: number;
  /** Solved ingest anchor-estimate bias [along-text, perpendicular], in
   *  units of (h × chars) and h. Present when the fit corrected for it;
   *  TEXT positions must be corrected by it before mapping (geometry must
   *  NOT — it carries no anchor estimate). See correctedTextPoint. */
  bias?: [number, number];
}

/** Bump when the fit algorithm changes materially. 2 = coherence-weighted
 *  family voting; 3 = + text-anchor bias solve (2026-08-29). */
export const REG_ALGO = 3;

/** A DXF text's position with the solved anchor bias applied. Geometry needs
 *  no such correction — only text carries the ingest's anchor estimate. */
export function correctedTextPoint(
  t: DxfTextEntry, key: string, bias?: [number, number],
): [number, number] {
  if (!bias) return [t.x, t.y];
  const rad = ((t.r ?? 0) * Math.PI) / 180;
  const h = t.h ?? 0;
  const w = h * key.length;
  const cos = Math.cos(rad), sin = Math.sin(rad);
  return [
    t.x + bias[0] * w * cos + bias[1] * (-h * sin),
    t.y + bias[0] * w * sin + bias[1] * (h * cos),
  ];
}

/** Cheap stable fingerprint of a label set (text + rounded positions). */
export function layoutEntitySig(
  entities: Array<Pick<LayoutTextEntity, 'normalizedText' | 'cx' | 'cy'>>,
): string {
  let h = 0x811c9dc5;
  for (const e of entities) {
    const s = `${e.normalizedText}|${e.cx.toFixed(1)}|${e.cy.toFixed(1)};`;
    for (let i = 0; i < s.length; i++) {
      h ^= s.charCodeAt(i);
      h = Math.imul(h, 0x01000193);
    }
  }
  return `${entities.length}:${(h >>> 0).toString(36)}`;
}

export interface DxfExtentsRegistration {
  kind: 'extents';
}

export type DxfRegistration = DxfLayoutRegistration | DxfExtentsRegistration;

// A fit needs enough anchors that one coincidental text match (a title-block
// string that happens to appear in both) cannot silently steer the transform:
// with 3+ the outlier trim can vote it off.
const MIN_MATCHES = 3;
// Outlier trim: drop pairs beyond 3× the median residual (with a floor so a
// razor-sharp fit doesn't trim on sub-point noise), refit, repeat.
const TRIM_FACTOR = 3;
const TRIM_FLOOR_PT = 2;
const MAX_TRIM_ROUNDS = 3;

/** Label comparison key: case/whitespace-insensitive. */
export function normalizeLabel(s: string): string {
  return s.replace(/\s+/g, ' ').trim().toUpperCase();
}

/** Texts that appear EXACTLY ONCE, keyed by normalized label. */
function uniqueByText<T>(items: T[], text: (t: T) => string): Map<string, T> {
  const counts = new Map<string, number>();
  for (const it of items) {
    const k = normalizeLabel(text(it));
    if (!k) continue;
    counts.set(k, (counts.get(k) ?? 0) + 1);
  }
  const out = new Map<string, T>();
  for (const it of items) {
    const k = normalizeLabel(text(it));
    if (k && counts.get(k) === 1) out.set(k, it);
  }
  return out;
}

/**
 * Closed-form least-squares similarity (Umeyama/Procrustes, no reflection):
 * src → dst as X = a·x − b·y + tx, Y = b·x + a·y + ty. Returned in the
 * [a, b, tx, c, d, ty] affine layout, i.e. [a, −b, tx, b, a, ty].
 */
export function fitSimilarity(
  src: Array<[number, number]>,
  dst: Array<[number, number]>,
): [number, number, number, number, number, number] | null {
  const n = src.length;
  if (n < 2 || dst.length !== n) return null;
  let scx = 0, scy = 0, dcx = 0, dcy = 0;
  for (let i = 0; i < n; i++) {
    scx += src[i][0]; scy += src[i][1];
    dcx += dst[i][0]; dcy += dst[i][1];
  }
  scx /= n; scy /= n; dcx /= n; dcy /= n;
  let sxx = 0, sxy = 0, norm = 0;
  for (let i = 0; i < n; i++) {
    const sx = src[i][0] - scx, sy = src[i][1] - scy;
    const dx = dst[i][0] - dcx, dy = dst[i][1] - dcy;
    sxx += sx * dx + sy * dy;
    sxy += sx * dy - sy * dx;
    norm += sx * sx + sy * sy;
  }
  if (norm < 1e-12) return null; // all source points coincide
  const a = sxx / norm;
  const b = sxy / norm;
  const tx = dcx - (a * scx - b * scy);
  const ty = dcy - (b * scx + a * scy);
  return [a, -b, tx, b, a, ty];
}

export function applyDxfMatrix(
  m: readonly number[],
  x: number,
  y: number,
): [number, number] {
  return [m[0] * x + m[1] * y + m[2], m[3] * x + m[4] * y + m[5]];
}

export interface MatchPair {
  key: string;
  src: [number, number];
  dst: [number, number];
  /** Along-text vector × (h × label length) — lever arm of the ingest's
   *  text-WIDTH estimate. */
  bias: [number, number];
  /** Perpendicular vector × h — lever arm of the baseline-vs-centre
   *  (vertical anchor) estimate. */
  biasPerp: [number, number];
}

/** Matched unique-label pairs between a DXF text set and layout entities. */
export function matchPairs(
  texts: DxfTextEntry[],
  entities: Array<Pick<LayoutTextEntity, 'normalizedText' | 'cx' | 'cy'>>,
): MatchPair[] {
  const dxfUnique = uniqueByText(texts, (t) => t.t);
  const layoutUnique = uniqueByText(entities, (e) => e.normalizedText);
  const pairs: MatchPair[] = [];
  for (const [key, d] of dxfUnique) {
    const e = layoutUnique.get(key);
    if (!e) continue;
    const rad = ((d.r ?? 0) * Math.PI) / 180;
    const h = d.h ?? 0;
    const w = h * key.length;
    const cos = Math.cos(rad), sin = Math.sin(rad);
    pairs.push({
      key, src: [d.x, d.y], dst: [e.cx, e.cy],
      bias: [w * cos, w * sin],
      biasPerp: [-h * sin, h * cos],
    });
  }
  return pairs;
}

// ── Text-width bias ─────────────────────────────────────────────────────────
//
// KALIA, proven twice over (2026-08-29): the ingest computes each text's
// visual centre from its anchor plus an ESTIMATED width. When that estimate
// is wrong for a drawing's font, every centre slides along its own text
// direction in proportion to (height × character count) — so the fit, which
// only sees text, absorbs the AVERAGE slide as a translation and puts the
// GEOMETRY that far off the drawing. Kalia: residual grew +0.32 pt/char, the
// fit translated −7.29 pt, and cross-correlating the DXF linework against the
// page's own ink measured +7.3 pt (2.7 m) — the same number from the other
// side. The text fit still read 0.26 pt, so nothing in the fit's own metrics
// could reveal it; only ink comparison could.
//
// Fix: solve one scalar per drawing alongside the similarity —
//   src'(c) = src + c · (h × len) · (cos r, sin r)
// picked by minimising the trimmed-LS residual on the base fit's inliers, and
// ACCEPTED only when it materially improves the fit (guard below). Every DXF
// site measured carries some bias (+0.08 … +0.37 pt/char); after correction
// every slope collapses to ≈0.
const BIAS_COARSE = 0.02;          // step, in units of (h×char) / h
const BIAS_RANGE = 15;             // ±0.30
const BIAS_FINE = 0.002;
const BIAS_MIN_IMPROVEMENT = 0.9;  // must cut rms by ≥10% …
const BIAS_MIN_BASE_PT = 0.05;     // … only when there IS an error to explain …
const BIAS_MIN_ABS_GAIN_PT = 0.02; // … and the gain is real, not float noise.
// Without the absolute guards a drawing whose fit is already exact has a
// baseline rms of ~1e-13, and any random c can "beat" it by a relative
// margin — a control test caught exactly that, 25 pt of spurious shift.

/** Apply an anchor-bias correction to the DXF side of each pair. */
export function shiftPairs(pairs: MatchPair[], cPar: number, cPerp: number): MatchPair[] {
  if (cPar === 0 && cPerp === 0) return pairs;
  return pairs.map((p) => ({
    ...p,
    src: [
      p.src[0] + cPar * p.bias[0] + cPerp * p.biasPerp[0],
      p.src[1] + cPar * p.bias[1] + cPerp * p.biasPerp[1],
    ] as [number, number],
  }));
}

/** Solve the two anchor-bias scalars on a set of (assumed inlier) pairs. */
function solveAnchorBias(pairs: MatchPair[]): { cPar: number; cPerp: number; rms: number; baseRms: number } {
  const base = trimmedCore(pairs);
  const baseRms = base ? base.rms : Infinity;
  let bestPar = 0, bestPerp = 0, bestRms = baseRms;
  for (let i = -BIAS_RANGE; i <= BIAS_RANGE; i++) {
    for (let j = -BIAS_RANGE; j <= BIAS_RANGE; j++) {
      const cPar = i * BIAS_COARSE, cPerp = j * BIAS_COARSE;
      const core = trimmedCore(shiftPairs(pairs, cPar, cPerp));
      if (core && core.rms < bestRms) { bestRms = core.rms; bestPar = cPar; bestPerp = cPerp; }
    }
  }
  for (let i = -9; i <= 9; i++) {
    for (let j = -9; j <= 9; j++) {
      const cPar = bestPar + i * BIAS_FINE, cPerp = bestPerp + j * BIAS_FINE;
      const core = trimmedCore(shiftPairs(pairs, cPar, cPerp));
      if (core && core.rms < bestRms) { bestRms = core.rms; bestPar = cPar; bestPerp = cPerp; }
    }
  }
  return { cPar: bestPar, cPerp: bestPerp, rms: bestRms, baseRms };
}

// ── Robust (coherence-weighted diversity-consensus) fit ─────────────────────
//
// Two production forensics shaped this (both visually ground-proofed against
// the original sheets):
//  - HATZAV: extractor corruption is family-correlated (ST labels displaced
//    in clusters), and a corrupted subset can form a RIGID fake consensus
//    that out-votes the truth under plain least-squares and magnitude-based
//    robust fits → family votes must be CAPPED.
//  - KALIA: the flat cap fails the opposite case — a site DOMINATED by one
//    razor-honest family (759 labels self-consistent to 0.23pt) had its
//    evidence crushed to 5 votes and a 195-inlier minority frame won.
// The discriminator: INTERNAL FAMILY COHERENCE. A family whose own members
// fit a single similarity (keep-rate ≥90%, rms ≤0.5pt) earns uncapped votes
// (Kalia's honest majority); an internally incoherent family (Hatzav's
// clustered ST) stays capped. Raw inlier count breaks remaining ties.
// Validated on all three corpora: Kalia 761/840 @0.26pt, Hatzav ≤0.85pt
// from the proven frame, NSM ≡ plain fit. Residual known risk: a family
// corrupted PERFECTLY uniformly would read as coherent — no such case
// observed; the PDF verdict-crop method (scratchpad forensics) resolves
// future disputes.

const RANSAC_ITERS = 1500;
const RANSAC_GATE_PT = 2.0;
// Votes are counted at a TIGHT core gate: a leverage compromise (slightly
// rotated frame holding two separated rigid populations at ~1.7pt each)
// can bridge the full gate but cannot hold anyone at core distance —
// found via the Kalia-miniature test, validated on all real corpora
// (Hatzav improved to ≤0.41pt from its visually proven frame).
const RANSAC_CORE_PT = 0.8;
const FAMILY_CAP = 5;

/** Label family: digits collapsed — one family = one drawing text style. */
export function familyOf(key: string): string {
  return key.replace(/\d+/g, '#').slice(0, 20);
}

// Family internal-coherence gate: keep-rate + rms bounds for a family's own
// trimmed similarity fit. See the module comment for why this decides the
// vote cap.
const COHERENT_MIN_SIZE = 8;
const COHERENT_KEEP_RATE = 0.9;
const COHERENT_RMS_PT = 0.5;

/** Families whose members are internally rigid-consistent (uncapped votes). */
function coherentFamilies(
  pairs: Array<{ key: string; src: [number, number]; dst: [number, number] }>,
  fams: string[],
): Set<string> {
  const byFam = new Map<string, Array<{ src: [number, number]; dst: [number, number] }>>();
  for (let i = 0; i < pairs.length; i++) {
    const arr = byFam.get(fams[i]);
    if (arr) arr.push(pairs[i]); else byFam.set(fams[i], [pairs[i]]);
  }
  const out = new Set<string>();
  for (const [fam, fp] of byFam) {
    if (fp.length < COHERENT_MIN_SIZE) continue;
    const core = trimmedCore(fp);
    if (core && core.kept >= COHERENT_KEEP_RATE * fp.length && core.rms <= COHERENT_RMS_PT) {
      out.add(fam);
    }
  }
  return out;
}

/** Trimmed least-squares core: matrix + rms + surviving count. */
function trimmedCore(
  pairsIn: Array<{ src: [number, number]; dst: [number, number] }>,
): { matrix: [number, number, number, number, number, number]; rms: number; kept: number } | null {
  let pairs = pairsIn;
  let matrix: [number, number, number, number, number, number] | null = null;
  let rms = 0;
  for (let round = 0; round <= MAX_TRIM_ROUNDS; round++) {
    matrix = fitSimilarity(pairs.map((p) => p.src), pairs.map((p) => p.dst));
    if (!matrix) return null;
    const m = matrix;
    const residuals = pairs.map((p) => {
      const [px, py] = applyDxfMatrix(m, p.src[0], p.src[1]);
      return Math.hypot(px - p.dst[0], py - p.dst[1]);
    });
    rms = Math.sqrt(residuals.reduce((s, r) => s + r * r, 0) / residuals.length);
    if (round === MAX_TRIM_ROUNDS) break;
    const sorted = [...residuals].sort((x, y) => x - y);
    const median = sorted[Math.floor(sorted.length / 2)];
    const cutoff = Math.max(TRIM_FACTOR * median, TRIM_FLOOR_PT);
    const kept = pairs.filter((_, i) => residuals[i] <= cutoff);
    if (kept.length === pairs.length || kept.length < MIN_MATCHES) break;
    pairs = kept;
  }
  return matrix ? { matrix, rms, kept: pairs.length } : null;
}

/** Deterministic LCG so fits are reproducible run-to-run.
 *
 *  Math.imul is REQUIRED: `s * 1103515245` exceeds 2^53 for a 31-bit seed,
 *  so plain multiplication loses the low bits and the generator degenerates
 *  to a near-constant sequence — the sampler then explores only a handful of
 *  hypotheses. (Caught by a control test whose exact-input fit was 25 pt off;
 *  the Python validation used arbitrary-precision ints and never saw it.) */
function* lcg(seed: number): Generator<number> {
  let s = seed | 0;
  while (true) {
    s = (Math.imul(s, 1103515245) + 12345) | 0;
    yield (s >>> 1);
  }
}

/** Exact similarity mapping p1→q1, p2→q2. */
function twoPointSimilarity(
  p1: [number, number], q1: [number, number],
  p2: [number, number], q2: [number, number],
): [number, number, number, number, number, number] | null {
  const dx = p2[0] - p1[0], dy = p2[1] - p1[1];
  const ex = q2[0] - q1[0], ey = q2[1] - q1[1];
  const den = dx * dx + dy * dy;
  if (den < 1e-12) return null;
  const a = (dx * ex + dy * ey) / den;
  const b = (dx * ey - dy * ex) / den;
  return [a, -b, q1[0] - (a * p1[0] - b * p1[1]), b, a, q1[1] - (b * p1[0] + a * p1[1])];
}

/**
 * Fit DXF → layout-page from matching label text. Diversity-consensus RANSAC
 * when there are enough pairs; the legacy trimmed least-squares below the
 * threshold (RANSAC needs a population to vote). Returns null under
 * MIN_MATCHES.
 */
/** The DXF's anchor population: hand-moved labels are INVISIBLE to the
 *  registration — excluded from the fit pairs AND from the staleness
 *  signature, so dragging labels neither sways nor re-triggers the
 *  alignment (owner 2026-09-03: "they are supposed to be separate"). */
export function dxfAnchorEntities<T extends { manuallyMoved?: boolean }>(entities: T[]): T[] {
  return entities.filter((e) => !e.manuallyMoved);
}

export function fitDxfToLayout(
  texts: DxfTextEntry[],
  entities: Array<Pick<LayoutTextEntity, 'normalizedText' | 'cx' | 'cy'> & { manuallyMoved?: boolean }>,
  layoutId: string,
): DxfLayoutRegistration | null {
  // Hand-dragged labels LEAVE the anchor set: a batch label drag is a large
  // coherent shift, and the RANSAC consensus follows the crowd — the owner
  // watched label cleanup pull the whole vector layer along (2026-09-02).
  // The entitySig is computed over the SAME filtered set (2026-09-03), so a
  // label's FIRST hand-move changes the sig once (it leaves the population)
  // and every later move of it is invisible to the auto-heal.
  const anchorEntities = dxfAnchorEntities(entities);
  const pairs = matchPairs(texts, anchorEntities);
  if (pairs.length < MIN_MATCHES) return null;
  const entitySig = layoutEntitySig(anchorEntities);
  if (pairs.length >= 8) {
    const robust = fitPairsRobust(pairs);
    if (robust) return { kind: 'layout', layoutId, ...robust, entitySig, algo: REG_ALGO };
  }
  const trimmed = fitPairsTrimmed(pairs, layoutId);
  return trimmed ? { ...trimmed, entitySig, algo: REG_ALGO } : null;
}

/** The validated robust core, shared with the label snap. */
export function fitPairsRobust(
  pairsIn: MatchPair[],
): { matrix: [number, number, number, number, number, number]; matched: number; rmsPt: number; bias?: [number, number] } | null {
  // Pass 1: consensus frame on the raw text anchors.
  const first = fitPairsRobustCore(pairsIn);
  if (!first) return null;
  // Pass 2: with a frame in hand, solve the ingest's anchor-estimate bias on
  // its inliers and refit — this is what puts the GEOMETRY on the drawing
  // (see the text-width bias note above). Applied only on a clear win.
  const inliers = pairsIn.filter((p) => {
    const [px, py] = applyDxfMatrix(first.matrix, p.src[0], p.src[1]);
    return Math.hypot(px - p.dst[0], py - p.dst[1]) <= RANSAC_GATE_PT;
  });
  if (inliers.length >= 8 && inliers.some((p) => p.bias[0] !== 0 || p.bias[1] !== 0)) {
    const { cPar, cPerp, rms, baseRms } = solveAnchorBias(inliers);
    if ((cPar !== 0 || cPerp !== 0)
        && baseRms >= BIAS_MIN_BASE_PT
        && baseRms - rms >= BIAS_MIN_ABS_GAIN_PT
        && rms < baseRms * BIAS_MIN_IMPROVEMENT) {
      const corrected = fitPairsRobustCore(shiftPairs(pairsIn, cPar, cPerp));
      if (corrected) return { ...corrected, bias: [cPar, cPerp] };
    }
  }
  return first;
}

function fitPairsRobustCore(
  pairs: MatchPair[],
): { matrix: [number, number, number, number, number, number]; matched: number; rmsPt: number } | null {
  const n = pairs.length;
  if (n < MIN_MATCHES) return null;
  const fams = pairs.map((p) => familyOf(p.key));
  const coherent = coherentFamilies(pairs, fams);
  const rnd = lcg(1234);
  const g2 = RANSAC_GATE_PT * RANSAC_GATE_PT;
  let bestM: [number, number, number, number, number, number] | null = null;
  let bestScore = -1;
  let bestRaw = -1;
  for (let it = 0; it < RANSAC_ITERS; it++) {
    const i = rnd.next().value! % n;
    const j = rnd.next().value! % n;
    if (i === j) continue;
    const m = twoPointSimilarity(pairs[i].src, pairs[i].dst, pairs[j].src, pairs[j].dst);
    if (!m) continue;
    const votes = new Map<string, number>();
    let raw = 0;
    const c2 = RANSAC_CORE_PT * RANSAC_CORE_PT;
    for (let k = 0; k < n; k++) {
      const [px, py] = applyDxfMatrix(m, pairs[k].src[0], pairs[k].src[1]);
      const dx = px - pairs[k].dst[0], dy = py - pairs[k].dst[1];
      const e2 = dx * dx + dy * dy;
      if (e2 <= g2) {
        raw++;
        if (e2 <= c2) votes.set(fams[k], (votes.get(fams[k]) ?? 0) + 1);
      }
    }
    let score = 0;
    for (const [fam, v] of votes) score += coherent.has(fam) ? v : Math.min(v, FAMILY_CAP);
    if (score > bestScore || (score === bestScore && raw > bestRaw)) {
      bestScore = score; bestRaw = raw; bestM = m;
    }
  }
  if (!bestM) return null;
  // Basin refinement on ALL gate-inliers (corrupted clusters sit far outside
  // the tight gate — no drift).
  let m = bestM;
  for (let round = 0; round < 3; round++) {
    const src: Array<[number, number]> = [];
    const dst: Array<[number, number]> = [];
    for (const p of pairs) {
      const [px, py] = applyDxfMatrix(m, p.src[0], p.src[1]);
      if (Math.hypot(px - p.dst[0], py - p.dst[1]) <= RANSAC_GATE_PT) {
        src.push(p.src); dst.push(p.dst);
      }
    }
    if (src.length < 3) break;
    const m2 = fitSimilarity(src, dst);
    if (!m2) break;
    m = m2;
  }
  let inl = 0, ss = 0;
  for (const p of pairs) {
    const [px, py] = applyDxfMatrix(m, p.src[0], p.src[1]);
    const e2 = (px - p.dst[0]) ** 2 + (py - p.dst[1]) ** 2;
    if (e2 <= g2) { inl++; ss += e2; }
  }
  if (inl < MIN_MATCHES) return null;
  return { matrix: m, matched: inl, rmsPt: Math.round(Math.sqrt(ss / inl) * 100) / 100 };
}

/** Legacy trimmed least-squares — small-pair fallback. */
function fitPairsTrimmed(
  pairsIn: Array<{ src: [number, number]; dst: [number, number] }>,
  layoutId: string,
): DxfLayoutRegistration | null {
  const core = trimmedCore(pairsIn);
  if (!core) return null;
  return {
    kind: 'layout',
    layoutId,
    matrix: core.matrix,
    matched: core.kept,
    rmsPt: Math.round(core.rms * 100) / 100,
  };
}

/**
 * Try every layout that has a transform, keep the best fit (most surviving
 * anchors; RMS breaks ties). Layouts without a transform can't render the
 * result, so they are not candidates even if their labels would match.
 */
export function fitDxfToBestLayout(
  texts: DxfTextEntry[],
  layouts: Array<{ id: string; transform?: LayoutTransform; textEntities: LayoutTextEntity[] }>,
): DxfLayoutRegistration | null {
  let best: DxfLayoutRegistration | null = null;
  for (const layout of layouts) {
    if (!layout.transform) continue;
    const fit = fitDxfToLayout(texts, layout.textEntities, layout.id);
    if (!fit) continue;
    if (!best || fit.matched > best.matched
        || (fit.matched === best.matched && fit.rmsPt < best.rmsPt)) {
      best = fit;
    }
  }
  return best;
}

// ─── Manual whole-drawing adjustment ────────────────────────────────────────
//
// The automatic fit gets the drawing close; the last metre is the human's.
// The correction lives in PAGE space, applied after the fit and before the
// layout transform, about the drawing's own centre — so it survives any
// re-fit underneath it (Re-align, Re-read, the staleness healer).

export interface DxfAdjust { dx: number; dy: number; rotDeg: number; scale: number }

export const IDENTITY_ADJUST: DxfAdjust = { dx: 0, dy: 0, rotDeg: 0, scale: 1 };

export function isIdentityAdjust(a?: DxfAdjust | null): boolean {
  return !a || (a.dx === 0 && a.dy === 0 && a.rotDeg === 0 && a.scale === 1);
}

/** Drawing centre in PAGE space — the pivot for rotate/scale. */
export function adjustPivot(
  extents: { minX: number; minY: number; maxX: number; maxY: number } | undefined,
  matrix: readonly number[],
): [number, number] {
  if (!extents) return [0, 0];
  return applyDxfMatrix(matrix,
    (extents.minX + extents.maxX) / 2,
    (extents.minY + extents.maxY) / 2);
}

/** Apply a manual adjustment to a PAGE-space point. */
export function applyAdjust(
  a: DxfAdjust | undefined, pivot: [number, number], x: number, y: number,
): [number, number] {
  if (isIdentityAdjust(a)) return [x, y];
  const { dx, dy, rotDeg, scale } = a!;
  const rad = (rotDeg * Math.PI) / 180;
  const cos = Math.cos(rad), sin = Math.sin(rad);
  const px = (x - pivot[0]) * scale, py = (y - pivot[1]) * scale;
  return [
    pivot[0] + px * cos - py * sin + dx,
    pivot[1] + px * sin + py * cos + dy,
  ];
}

// ─── Local (zone) corrections ───────────────────────────────────────────────

export interface DxfZone {
  id: string;
  /** First (or only) part. Kept as the primary field so earlier zones read
   *  unchanged; `polys` carries the rest for a multi-part selection. */
  poly: Array<[number, number]>;
  /** Additional parts, added by Ctrl-clicking more elements into this zone. */
  polys?: Array<Array<[number, number]>>;
  dx: number;
  dy: number;
  /** AutoCAD's own convention, because these are CAD users looking at CAD
   *  drawings: 'window' (drag left→right) moves only what is ENTIRELY
   *  inside; 'crossing' (drag right→left) moves anything the box touches.
   *  Absent = window — the safe reading of "I boxed these". */
  mode?: 'window' | 'crossing';
}

/** Ray-casting point-in-polygon, PAGE space. */
export function pointInPoly(poly: Array<[number, number]>, x: number, y: number): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i], [xj, yj] = poly[j];
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi + 1e-12) + xi) {
      inside = !inside;
    }
  }
  return inside;
}

/**
 * Delta for a whole feature, given its points in PAGE space. First matching
 * zone wins, so a zone drawn inside another refines it.
 *
 * Containment is per-MODE, not by centroid: a centroid test moved an entire
 * table because its middle happened to fall in a box drawn around something
 * else (owner-reported, and rightly surprising). 'window' requires the whole
 * feature inside; 'crossing' needs only a touch.
 */
/** Does this zone capture a feature with these PAGE-space points? Same rule
 *  the renderer applies, exposed so the selection highlight can never
 *  disagree with what actually moves. */
export function zonePolys(z: DxfZone): Array<Array<[number, number]>> {
  return z.polys?.length ? [z.poly, ...z.polys] : [z.poly];
}

export function zoneCaptures(
  z: DxfZone, pts: Array<[number, number]> | GeoJSON.Position[],
): boolean {
  if (!pts.length) return false;
  // Any PART capturing is capture — the parts are one selection.
  for (const poly of zonePolys(z)) {
    if (z.mode === 'crossing') {
      for (const p of pts) if (pointInPoly(poly, p[0], p[1])) return true;
    } else {
      let all = true;
      for (const p of pts) {
        if (!pointInPoly(poly, p[0], p[1])) { all = false; break; }
      }
      if (all) return true;
    }
  }
  return false;
}

export function zoneDeltaForPoints(
  zones: DxfZone[] | undefined, pts: Array<[number, number]> | GeoJSON.Position[],
): [number, number] {
  if (!zones?.length || !pts.length) return [0, 0];
  for (const z of zones) {
    if (zoneCaptures(z, pts)) return [z.dx, z.dy];
  }
  return [0, 0];
}

// ─── Render-time mappers ────────────────────────────────────────────────────

export type DxfToMap = (x: number, y: number) => [number, number];

/** Layout-registered mapper: DXF → layout PDF pt → map via the layout's transform. */
export function layoutMapper(
  reg: DxfLayoutRegistration,
  layoutTransform: LayoutTransform,
): DxfToMap {
  return (x, y) => {
    const [px, py] = applyDxfMatrix(reg.matrix, x, y);
    return transformPoint(layoutTransform, px, py);
  };
}

/**
 * Extents to stamp for a PDF-derived ingest (R3-D). The worker's coordinates
 * are PDF points in the displayed page frame (origin bottom-left, y up) — the
 * frame the bake rasterizes — and the plan overlay quad is the CROPPED page
 * (a crop moves the corners). So the crop window, expressed in page points,
 * is exactly what extentsMapper must stretch onto the quad: every vector then
 * lands on the pixel it was rasterized at. No crop = the whole page.
 * `v` runs down the raster (bake crop convention); page y runs up.
 */
export function pdfPageExtents(
  page: { widthPt: number; heightPt: number },
  crop: { u0: number; v0: number; u1: number; v1: number },
): { minX: number; minY: number; maxX: number; maxY: number } | null {
  const w = page.widthPt;
  const h = page.heightPt;
  if (!(w > 0) || !(h > 0)) return null;
  return { minX: crop.u0 * w, maxX: crop.u1 * w, minY: (1 - crop.v1) * h, maxY: (1 - crop.v0) * h };
}

/**
 * Extents-fallback mapper: stretch the DXF's global extents onto the plan
 * overlay quad ([TL, TR, BR, BL], u → right, v → down). DXF y grows UP, quad
 * v grows DOWN, hence the flip.
 */
export function extentsMapper(
  extents: { minX: number; minY: number; maxX: number; maxY: number },
  quad: [[number, number], [number, number], [number, number], [number, number]],
): DxfToMap | null {
  const w = extents.maxX - extents.minX;
  const h = extents.maxY - extents.minY;
  if (!(w > 0) || !(h > 0)) return null;
  const [tl, tr, br, bl] = quad;
  return (x, y) => {
    const u = (x - extents.minX) / w;
    const v = 1 - (y - extents.minY) / h;
    return [
      (1 - u) * (1 - v) * tl[0] + u * (1 - v) * tr[0] + u * v * br[0] + (1 - u) * v * bl[0],
      (1 - u) * (1 - v) * tl[1] + u * (1 - v) * tr[1] + u * v * br[1] + (1 - u) * v * bl[1],
    ];
  };
}
